import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  Stack,
  Chip,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ScienceIcon from '@mui/icons-material/Science';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ChangeTypeBadge } from './ChangeTypeBadge';
import { SeverityBadge } from './SeverityBadge';
import { ChangeDiffViewer } from './ChangeDiffViewer';
import type { PageChangeEvent } from '../../types/changeDetection.types';

interface ChangeEventCardProps {
  event: PageChangeEvent;
  onCreateExperiment?: (eventId: number) => void;
}

const formatDateTime = (isoString: string): string => {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const canCreateExperiment = (event: PageChangeEvent): boolean => {
  // Only META and CONTENT changes are eligible for experiments
  return event.changeTypes.some(type => type === 'META' || type === 'CONTENT');
};

export const ChangeEventCard: React.FC<ChangeEventCardProps> = ({
  event,
  onCreateExperiment
}) => {
  const [showDiff, setShowDiff] = useState(false);
  const isExperimentEligible = canCreateExperiment(event);

  return (
    <Card sx={{ mb: 2, borderLeft: 4, borderColor: event.severity === 'HIGH' ? 'error.main' : 'divider' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(event.detectedAt)}
            </Typography>
            <SeverityBadge severity={event.severity} size="small" />
          </Box>
        }
        subheader={
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {event.changeTypes.map(type => (
              <ChangeTypeBadge key={type} type={type} size="small" />
            ))}
            {event.commitSha && (
              <Chip
                label={`Commit: ${event.commitSha.substring(0, 7)}`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: 'monospace' }}
              />
            )}
          </Stack>
        }
        sx={{ pb: 1 }}
      />

      <CardContent sx={{ pt: 0 }}>
        <Button
          variant="text"
          size="small"
          onClick={() => setShowDiff(!showDiff)}
          endIcon={showDiff ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showDiff ? 'Hide' : 'View'} Changes
        </Button>

        <Collapse in={showDiff}>
          <Box sx={{ mt: 2 }}>
            <ChangeDiffViewer diff={event.diffJson} />
          </Box>
        </Collapse>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box>
          {isExperimentEligible && onCreateExperiment && (
            <Button
              variant="contained"
              size="small"
              startIcon={<ScienceIcon />}
              onClick={() => onCreateExperiment(event.id)}
            >
              Create Experiment
            </Button>
          )}
        </Box>
        <Button
          variant="text"
          size="small"
          endIcon={<OpenInNewIcon />}
          disabled
        >
          View Details
        </Button>
      </CardActions>
    </Card>
  );
};
